/**
 * Topic display-label lookup (contracts-plan.md §9.3; session 4 FACTS 2,
 * SHARED CONTRACT BLOCK 2). `REVIEW_TOPICS`
 * (backend/src/services/contracts/clauses/review.js) is the only place the
 * 24-slug vocabulary is DEFINED — this file never re-derives, re-orders or
 * hard-codes it as a closed set. `TOPIC_LABELS` below names all 24 slugs
 * plus the literal `unassigned` bucket with a human label; `topicLabel()`
 * falls back to a humanised slug for anything this table does not know, so
 * a future backend addition still renders here instead of becoming a
 * TypeScript error (that is why `ClauseTopic` in `api/clauses.ts` is typed
 * as a bare `string`, not a union).
 *
 * A topic is always labelled `inferred` (§9.3, G8) — callers show that
 * sentence beside the label; this file only supplies the label text.
 */

const TOPIC_LABELS: Record<string, string> = {
  payment_days: 'Payment days',
  payment_frequency: 'Payment frequency',
  holdback_pct: 'Holdback percent',
  holdback_release: 'Holdback release',
  overhead_pct: 'Overhead percent',
  start_up_fee: 'Start-up fee',
  screen_fail_ratio: 'Screen-fail ratio',
  screen_fail_cap: 'Screen-fail cap',
  screen_fail_payment: 'Screen-fail payment',
  indemnification: 'Indemnification',
  subject_injury: 'Subject injury',
  protocol_deviation_notice: 'Protocol-deviation notice',
  record_retention: 'Record retention',
  disposal_notice: 'Disposal notice',
  publication_review: 'Publication review',
  publication_deferral: 'Publication deferral',
  publication_multicenter: 'Publication (multi-center)',
  termination_notice: 'Termination notice',
  termination_cure: 'Termination cure',
  accrued_costs: 'Accrued costs',
  governing_law: 'Governing law',
  audit_timing: 'Audit timing',
  invoiceables: 'Invoiceables',
  dispute_resolution: 'Dispute resolution',
  // Not a member of REVIEW_TOPICS (session 4 SHARED CONTRACT BLOCK 2): the
  // bucket for a clause whose topic came back null. Never carryable.
  unassigned: 'Unassigned',
};

/** underscores -> spaces, first letter capitalised. Used only when a slug is not in TOPIC_LABELS above. */
function humanize(slug: string): string {
  const words = slug.split('_').filter(Boolean);
  if (words.length === 0) return slug;
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/** Display label for a topic slug (or `null`/`undefined`, rendered as the unassigned label). Falls back to a humanised slug for a slug this table does not know (FACTS 2) — never throws, never renders blank. */
export function topicLabel(slug: string | null | undefined): string {
  if (!slug) return TOPIC_LABELS.unassigned;
  return TOPIC_LABELS[slug] ?? humanize(slug);
}
